import { FileText, Shield, ShieldAlert, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { ArchiveQueryResponse } from '@/types/api';
import { cn } from '@/lib/utils';

interface ArchiveQueryResultProps {
  readonly result: ArchiveQueryResponse;
}

export function ArchiveQueryResult({ result }: ArchiveQueryResultProps) {
  const tamper = result.tamperDetectionResult;
  const isClean = tamper.compromised === 0;

  return (
    <div className="space-y-4">
      {/* Answer */}
      <Card>
        <CardContent className="p-5">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {result.answer}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span>Intent: {result.intent}</span>
            <span>
              {new Date(result.executedAt).toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Sources */}
      {result.sources.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sources ({result.sources.length})
          </h4>
          <div className="space-y-2">
            {result.sources.map((src) => (
              <div
                key={src.cardId}
                className="flex items-center gap-3 rounded-lg border border-border px-4 py-3"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {src.documentTitle}
                  </p>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="capitalize">{src.department}</span>
                    <span>v{src.versionId.slice(0, 8)}</span>
                    <span>{new Date(src.versionedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1">
                    <div
                      className={cn(
                        'h-1.5 rounded-full',
                        src.relevanceScore >= 0.8
                          ? 'bg-green-400'
                          : src.relevanceScore >= 0.5
                            ? 'bg-amber-400'
                            : 'bg-red-400',
                      )}
                      style={{ width: `${Math.max(src.relevanceScore * 48, 8)}px` }}
                    />
                    <span className="text-xs text-muted-foreground">
                      {Math.round(src.relevanceScore * 100)}%
                    </span>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tamper Detection */}
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border px-4 py-3',
          isClean
            ? 'border-green-500/20 bg-green-500/5'
            : 'border-red-500/20 bg-red-500/5',
        )}
      >
        {isClean ? (
          <Shield className="h-4 w-4 shrink-0 text-green-400" />
        ) : (
          <ShieldAlert className="h-4 w-4 shrink-0 text-red-400" />
        )}
        <div className="text-sm">
          {isClean ? (
            <p className="text-green-400">
              Tamper detection passed — {tamper.checked} version{tamper.checked === 1 ? '' : 's'} verified.
            </p>
          ) : (
            <>
              <p className="font-medium text-red-400">
                Tamper detection warning — {tamper.compromised} of {tamper.checked} version{tamper.checked === 1 ? '' : 's'} may be compromised.
              </p>
              <p className="mt-0.5 text-xs text-red-400/80">
                Affected: {tamper.compromisedIds.join(', ')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
